/* eslint-disable no-param-reassign */
/**
 * Manages all the request/response interceptors used by the API.
 *
 * @author Hugo Azevedo
 */
import { CancelToken } from 'axios';
import flow from 'lodash.flow';

export function insertAuthInterceptor(options) {
  const { authOptions, errorStatusInterceptors } = options;

  return function authInterceptor(config) {
    const authentication = authOptions ? authOptions.getSessionId() : null;

    const cancelRequest = !authentication && !errorStatusInterceptors.authNotFound(config);

    // This stops the request from being made.
    if (cancelRequest) {
      return {
        ...config,
        cancelToken: new CancelToken(cancel => cancel('No authorization token found')),
      };
    }

    authentication && (config.headers[authOptions.getTokenName()] = authentication);

    return config;
  };
}

export function responseErrorInterceptor(options) {
  const { errorStatusInterceptors } = options;

  return function errorInterceptor(err) {
    let errStatus = err && err.status;

    if (!errStatus && err.response) {
      errStatus = err.response.status;
    }

    if (errorStatusInterceptors && errStatus in errorStatusInterceptors) {
      return errorStatusInterceptors[errStatus](err);
    }

    return Promise.reject(err);
  };
}

export function addRequestInterceptors(axios, requestInterceptors) {
  requestInterceptors.forEach((interceptor) => {
    axios.interceptors.request.use(interceptor);
  });
  return axios;
}

export function addResponseInterceptors(axios, responseInterceptors) {
  responseInterceptors.forEach(([interceptor, errorInterceptor]) => {
    axios.interceptors.response.use(interceptor, errorInterceptor);
  });
  return axios;
}

function createRequestInterceptorsArray(options) {
  const { interceptors } = options;
  if (interceptors && interceptors.request) {
    return [flow(interceptors.request), insertAuthInterceptor(options)];
  }
  return [insertAuthInterceptor(options)];
}

function createResponseInterceptorsArray(options) {
  const { interceptors } = options;
  if (interceptors && interceptors.response) {
    const errorInterceptors = interceptors.response.error
      ? flow(interceptors.response.error)
      : null;

    return [
      [
        flow(interceptors.response.ok),
        errorInterceptors
          ? flow(errorInterceptors, responseErrorInterceptor(options))
          : responseErrorInterceptor(options),
      ],
    ];
  }
  return [[undefined, responseErrorInterceptor(options)]];
}

export function withDefaultInterceptors(axios, options) {
  addRequestInterceptors(
    axios,
    createRequestInterceptorsArray(options),
  );

  addResponseInterceptors(
    axios,
    createResponseInterceptorsArray(options),
  );

  return axios;
}
